import React from 'react'
import HtmlReactParser, {
  domToReact,
  Element,
  type HTMLReactParserOptions,
} from 'html-react-parser'
import { ExternalLink, Link } from 'suomifi-ui-components'
import { createDomPurifyForEnv } from 'common/src/vtj/dom-purify.util'
import { createI18nInstance } from 'common-translations/src/vtj/i18next-utils'
import commonTranslations from 'common-translations/src/vtj/translations-bundle'

const DOMPurify = createDomPurifyForEnv()
const i18n = createI18nInstance({
  defaultBundle: commonTranslations,
})

/*
 * Predicate that returns `true` when `inputUrl` is a relative url, `false`
 * when it can be constructed as an absolute url
 */
export const isUrlRelative = (inputUrl: string): boolean => {
  try {
    return (
      new URL(inputUrl, document.baseURI).hostname ===
      new URL(document.baseURI).hostname
    )
  } catch (e) {
    return false
  }
}

export const transform: HTMLReactParserOptions['replace'] = (node) => {
  if (!(node instanceof Element) || node.type !== 'tag' || node.name !== 'a') {
    return undefined
  }

  const { 'data-test-id': dataTestId, href, hreflang, lang } = node.attribs

  if (!href) {
    return undefined
  }

  const children = domToReact(node.children, parserOptions)

  if (isUrlRelative(href)) {
    return (
      <Link
        data-test-id={dataTestId}
        href={href}
        hrefLang={hreflang}
        lang={lang}
      >
        {children}
      </Link>
    )
  } else {
    return (
      <ExternalLink
        data-test-id={dataTestId}
        href={href}
        hrefLang={hreflang}
        lang={lang}
        labelNewWindow={i18n.t('opensInNewWindow')}
        hideIcon
      >
        {children}
      </ExternalLink>
    )
  }
}

const parserOptions: HTMLReactParserOptions = {
  replace: transform,
}

const sanitizeOptions = {
  ALLOWED_TAGS: [
    'div',
    'span',
    'p',
    'strong',
    'b',
    'i',
    'a',
    'br',
    'ul',
    'li',
    '#text', // pseudoelement for text content, used by dompurify
  ],
  KEEP_CONTENT: false,
  FORBID_ATTR: ['target'],
} satisfies DOMPurify.Config

export const parse = (html: string): ReturnType<typeof HtmlReactParser> => {
  const sanitised = DOMPurify.sanitize(html, sanitizeOptions)
  return HtmlReactParser(sanitised, parserOptions)
}
